/* Product card colour swatches: turn the app's strip into a slider
   ------------------------------------------------------------------
   G: Combined Listings injects colour swatches into product cards as a
   plain flex row (.mpg-swatch__nav) that wraps onto extra rows. This
   progressively enhances that row into a Swiper slider, using the
   swiper-bundle already loaded globally in layout/theme.liquid.

   The markup is app-owned and injected after load, so everything here
   is defensive: we observe for strips appearing, mark what we have
   already handled, and rebuild if the app replaces a strip. If Swiper
   is missing or this script fails, the CSS fallback leaves the strip
   as a single-line horizontal scroller, so nothing breaks. */

(function () {
	'use strict';

	var ENHANCED = 'data-ws-swatch-slider';
	var CARD_ROOT = '.mpg-swatch.mpg-card';
	var instances = new WeakMap();

	// Same caret the theme uses in snippets/icon-caret.liquid, rotated by CSS
	var CARET =
		'<svg aria-hidden="true" focusable="false" viewBox="0 0 12 13">' +
		'<path d="M6.00012 7.08584L8.47512 4.61084L9.18212 5.31784L6.00012 8.49984L2.81812 5.31784L3.52512 4.61084L6.00012 7.08584Z" fill="currentColor"/>' +
		'</svg>';

	function makeArrow(direction) {
		var button = document.createElement('button');
		button.type = 'button';
		button.className =
			'ws-swatches__arrow ws-swatches__arrow--' + direction;
		button.setAttribute(
			'aria-label',
			direction === 'prev' ? 'Previous colours' : 'Next colours'
		);
		button.innerHTML = '<span aria-hidden="true">' + CARET + '</span>';
		return button;
	}

	/* Put the colourway this card is for first. Done in the DOM rather
	   than with CSS order so that Swiper measures slides in the order it
	   shows them, and so tab order matches what is on screen. */
	function moveSelectedFirst(nav) {
		var selected = nav.querySelector('.mpg-swatch__option.selected');
		if (selected && selected !== nav.firstElementChild) {
			nav.insertBefore(selected, nav.firstElementChild);
		}
	}

	/* Cards are not one width. The popular-products masonry runs from
	   about 130px to 620px, so a fixed swatch is either huge on the small
	   cards or lost on the big ones, and on the smallest the two arrows
	   leave room for a single swatch. Scale to the strip we actually got. */
	function applyDensity(root, shell, viewport) {
		var width = viewport.getBoundingClientRect().width;
		if (!width) return false;

		var compact = width < 170;
		var size = compact ? 3.2 : width < 300 ? 4 : 4.8;

		root.style.setProperty('--ws-swatch-size', size + 'rem');
		shell.classList.toggle('ws-swatches--compact', compact);
		return true;
	}

	/* Clicking a swatch should open that colourway's product page rather
	   than swapping the card in place. The app publishes its own data at
	   window.mpg.groups: a group whose id matches the card's group-id,
	   holding products whose `options` array carries the same id as the
	   swatch's v-id. So the destination can be resolved before the app
	   reacts, and we navigate straight there.

	   If anything is missing we return null and simply do not intercept,
	   which leaves the app's original swap behaviour intact. */
	function resolveHandle(root, option) {
		var groupId = root.getAttribute('group-id');
		var optionId = option.getAttribute('v-id');
		if (!groupId || !optionId) return null;

		var mpg = window.mpg;
		if (!mpg || !Array.isArray(mpg.groups)) return null;

		var group = null;
		for (var i = 0; i < mpg.groups.length; i++) {
			if (mpg.groups[i] && mpg.groups[i].id === groupId) {
				group = mpg.groups[i];
				break;
			}
		}
		if (!group || !Array.isArray(group.products)) return null;

		for (var j = 0; j < group.products.length; j++) {
			var product = group.products[j];
			if (
				product &&
				Array.isArray(product.options) &&
				product.options.indexOf(optionId) !== -1 &&
				product.handle
			) {
				return product.handle;
			}
		}
		return null;
	}

	/* Reuse whatever prefix the theme's own product links carry, so any
	   market or locale path survives. */
	function productUrl(root, handle) {
		var card = root.closest('.card-wrapper');
		var existing = card && card.querySelector('a[href*="/products/"]');
		var prefix = '';

		if (existing) {
			var href = existing.getAttribute('href') || '';
			var at = href.indexOf('/products/');
			if (at > 0) prefix = href.slice(0, at);
		}

		return prefix + '/products/' + handle;
	}

	/* Capture on document so this runs before the app's own handler on
	   the swatch, and stops the swap from happening at all. */
	document.addEventListener(
		'click',
		function (event) {
			var option =
				event.target &&
				event.target.closest &&
				event.target.closest('.mpg-swatch__option');
			if (!option) return;

			// Cards only. The product page's swatches keep app behaviour.
			var root = option.closest(CARD_ROOT);
			if (!root) return;

			var handle = resolveHandle(root, option);
			if (!handle) return;

			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();

			var url = productUrl(root, handle);

			// Honour the usual "open in a new tab" modifiers
			if (event.metaKey || event.ctrlKey || event.shiftKey) {
				window.open(url, '_blank');
			} else {
				window.location.href = url;
			}
		},
		true
	);

	function enhance(root) {
		if (root.hasAttribute(ENHANCED)) return;

		var nav = root.querySelector('.mpg-swatch__nav');
		if (!nav) return;

		var options = nav.querySelectorAll('.mpg-swatch__option');
		if (!options.length) return;

		root.setAttribute(ENHANCED, '');
		moveSelectedFirst(nav);

		// Shell: arrow, viewport, arrow. Arrows flank rather than overlay,
		// because a 48px strip is too short to cover a swatch with a button.
		var shell = document.createElement('div');
		shell.className = 'ws-swatches';

		var viewport = document.createElement('div');
		viewport.className = 'ws-swatches__viewport swiper';

		nav.parentNode.insertBefore(shell, nav);
		shell.appendChild(viewport);
		viewport.appendChild(nav);

		nav.classList.add('swiper-wrapper');
		Array.prototype.forEach.call(options, function (option) {
			option.classList.add('swiper-slide');
			// It navigates now, so announce it as a link
			option.setAttribute('role', 'link');
		});

		var prev = makeArrow('prev');
		var next = makeArrow('next');
		shell.insertBefore(prev, viewport);
		shell.appendChild(next);

		root.classList.add('ws-swatches-ready');
		applyDensity(root, shell, viewport);

		var swiper = new Swiper(viewport, {
			slidesPerView: 'auto',
			spaceBetween: 8,
			// Locks the slider and hides the arrows when everything fits
			watchOverflow: true,
			slidesPerGroupAuto: true,
			navigation: { nextEl: next, prevEl: prev },
			// The swatches are already focusable controls with their own
			// labels; Swiper's a11y module would talk over them.
			a11y: { enabled: false }
		});

		instances.set(root, swiper);

		/* Swiper only toggles a class at the ends. Mirror it into
		   aria-disabled and drop the button out of the tab order so it is
		   not a silent dead control (same as the Back To School rail). */
		function syncArrows() {
			[prev, next].forEach(function (button) {
				var disabled =
					button.classList.contains('swiper-button-disabled') ||
					button.classList.contains('swiper-button-lock');
				button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
				button.tabIndex = disabled ? -1 : 0;
			});
		}

		syncArrows();

		// Re-scale on resize, then let Swiper remeasure the new slide width
		swiper.on('resize', function () {
			if (applyDensity(root, shell, viewport)) swiper.update();
		});

		['slideChange', 'update', 'resize', 'toEdge', 'fromEdge', 'lock', 'unlock'].forEach(
			function (event) {
				swiper.on(event, syncArrows);
			}
		);
	}

	function scan() {
		Array.prototype.forEach.call(
			document.querySelectorAll(CARD_ROOT),
			enhance
		);
	}

	/* The app injects strips after load, and again when cards are replaced
	   (pagination, filtering, quick view), so watch rather than run once. */
	var pending = null;
	function scheduleScan() {
		if (pending) return;
		/* setTimeout, not requestAnimationFrame: rAF is throttled to zero
		   in a hidden or background tab, so a collection page opened in a
		   background tab would never get its sliders built. */
		pending = window.setTimeout(function () {
			pending = null;
			scan();
		}, 50);
	}

	function start() {
		if (typeof Swiper === 'undefined') {
			// swiper-bundle is deferred; try again once it has parsed
			window.setTimeout(start, 100);
			return;
		}

		scan();

		new MutationObserver(function (mutations) {
			for (var i = 0; i < mutations.length; i++) {
				if (mutations[i].addedNodes.length) {
					scheduleScan();
					return;
				}
			}
		}).observe(document.body, { childList: true, subtree: true });
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', start);
	} else {
		start();
	}
})();
